import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api, { TokenStorage, baseURL } from '../utils/api';
import AuthShell from './common/AuthShell';
import { Button, FormField } from './ui';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const routeMap = {
    connect_bank: '/connect-bank',
    review_detected: '/review-detected',
    complete: '/dashboard',
  };

  const resolvePostLoginDestination = async () => {
    const urlRedirect = params.get('redirect');
    if (urlRedirect) return urlRedirect;

    const returnTo = sessionStorage.getItem('returnTo');
    if (returnTo) {
      sessionStorage.removeItem('returnTo');
      return returnTo;
    }

    try {
      const { data } = await api.get('/api/user/onboarding');
      const step = data?.step;
      if (step && routeMap[step]) return routeMap[step];
    } catch (e) {
      // fall through to default
    }

    return '/dashboard';
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    try {
      const res = await api.post('/auth/login', { email, password });
      TokenStorage.setToken(res.data.accessToken);
      const dest = await resolvePostLoginDestination();
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Login failed';
      setError(msg);
      setFieldErrors({
        email: msg.toLowerCase().includes('email') ? msg : undefined,
        password: msg.toLowerCase().includes('password') ? msg : undefined,
      });
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${baseURL}/api/auth/google`;
  };

  return (
    <AuthShell title="Log in" subtitle="Enter your email address">
      {error && (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <div className="font-semibold">Login Failed</div>
          <p className="mt-1">{error}</p>
        </div>
      )}

      <form onSubmit={handleLocalLogin} className="space-y-5 sm:space-y-6">
        <FormField
          label="Email"
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@securethebag.ai"
          required
          autoComplete="email"
          error={fieldErrors.email}
        />

        <div>
          <FormField
            label="Password"
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            autoComplete="current-password"
            error={fieldErrors.password}
          />
          <div className="mt-2">
            <Link
              to="/forgot-password-request"
              className="text-base sm:text-sm text-blue-700 hover:text-blue-800 transition-colors inline-flex items-center min-h-[44px] sm:min-h-0 font-medium"
            >
              Forgot Password?
            </Link>
          </div>
        </div>

        <Button type="submit" variant="primary" fullWidth>
          Sign In
        </Button>

        <div className="flex items-center gap-4 my-4">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-sm text-gray-500">or</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <Button type="button" variant="secondary" fullWidth onClick={handleGoogleLogin}>
          Log in with Google
        </Button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-base sm:text-sm text-gray-600">
          Don't have an account?{' '}
          <Link
            to="/signup"
            className="text-blue-700 hover:text-blue-800 font-medium transition-colors inline-flex items-center min-h-[44px] sm:min-h-0"
          >
            Sign up
          </Link>
        </p>
      </div>
    </AuthShell>
  );
};

export default LoginPage;
